export const pagination = {
    name: 'pagination',
    component() {
        return {
            current_page: 0,
            items_total: 0,
            per_page: 0,
            pages: 0,
            pageOffset: 1,
            showViewMore: false,
            currentItemsViewed: 0,
            object_name: '',
            isInit: false,
            collectionCurrentlyViewed: null,
            init(items_total, per_page, useViewMore = false, object_name = 'products', currentResultsCount = null) {
                
                if (items_total !== undefined && per_page) {
                    this.items_total = items_total;
                    this.object_name = object_name;
                    this.per_page = per_page;
                    this.pages = Math.ceil(items_total / per_page);
                    this.showViewMore = useViewMore;
                    this.currentItemsViewed = currentResultsCount ?? Math.min(this.per_page, items_total);
                    this.current_page = 0;

                    const params = new URLSearchParams(window.location.search);
                    const pageFromUrl = parseInt(params.get('page'), 10);
                    if (!isNaN(pageFromUrl) && pageFromUrl > 1) {
                        this.current_page = pageFromUrl - 1;
                    }

                    // Reset collection currently viewed when reinitializing
                    this.collectionCurrentlyViewed = null;

                    if (this.current_page + this.pageOffset === this.pages) {
                        this.currentItemsViewed = this.items_total;
                    }

                    this.$nextTick(() => {
                        if (this.showViewMore) {
                            this.renderViewMoreButton();
                        } else {
                            this.renderPagination();
                        }
                    });
                    
                    window.addEventListener('current-products-updated', this.handleCurrentlyViewed.bind(this));
                    window.dispatchEvent(new CustomEvent('pagination-have-loaded'));
                }

            },
            handleCurrentlyViewed(event) {
                this.collectionCurrentlyViewed = event.detail.totalViewed;
                this.renderViewMoreButton();
            },
            renderViewMoreButton() {
                const paginationContainer = this.$refs.paginationContainer;
                paginationContainer.innerHTML = ''; // Clear existing controls
                paginationContainer.className = 'view-more';

                const viewMoreText = document.createElement('p');
                viewMoreText.className = 'view-more__text';

                const viewedCount = this.collectionCurrentlyViewed ? this.collectionCurrentlyViewed : this.currentItemsViewed;

                // Handle zero products case
                if (this.items_total === 0) {
                    viewMoreText.innerHTML = `No ${this.object_name} found`;
                    paginationContainer.appendChild(viewMoreText);
                    return; // Exit early, don't show progress bar or load more button
                }

                // Hide the message until we have valid viewed count data
                if (viewedCount === null || viewedCount === undefined || viewedCount === 0) {
                    return; // Don't render anything until we have valid data
                }

                viewMoreText.innerHTML = `You've viewed <strong>${viewedCount} of ${this.items_total}</strong> ${this.object_name}`;
                paginationContainer.appendChild(viewMoreText);

                const progressBar = document.createElement('div');
                progressBar.id = 'progress-bar';
                progressBar.className = 'view-more__progress-bar';
                paginationContainer.appendChild(progressBar);

                const progressBarWrapper = document.getElementById('progress-bar');
                const progressBarMarker = document.createElement('div');
                progressBarMarker.className = 'view-more__progress-bar-marker';
                progressBarMarker.style.width = `${Math.min(viewedCount / this.items_total * 100, 100)}%`;
                progressBarWrapper.appendChild(progressBarMarker);

                const renderLoadMore = this.collectionCurrentlyViewed
                    ? this.collectionCurrentlyViewed < this.items_total
                    : (this.current_page + this.pageOffset) < this.pages

                if (renderLoadMore) {
                    const viewMoreButton = document.createElement('button');
                    viewMoreButton.innerText = 'Load More';
                    viewMoreButton.className = 'view-more__button btn-light';
                    viewMoreButton.onclick = () => {
                        this.nextPage();

                        // Update URL with new page number for SEO crawlability
                        const nextPageNumber = this.current_page + this.pageOffset;
                        const url = new URL(window.location);
                        url.searchParams.set('page', nextPageNumber);
                        window.history.pushState({}, '', url.toString());

                        // Update canonical link to reflect new page URL
                        let canonicalTag = document.querySelector('link[rel="canonical"]');
                        if (!canonicalTag) {
                            canonicalTag = document.createElement('link');
                            canonicalTag.setAttribute('rel', 'canonical');
                            document.head.appendChild(canonicalTag);
                        }
                        canonicalTag.setAttribute('href', url.toString());
                    };
                    paginationContainer.appendChild(viewMoreButton);
                }
            },
            renderPagination() {
                const paginationContainer = this.$refs.paginationContainer;
                paginationContainer.innerHTML = ''; // Clear existing pagination

                // Handle zero products case
                if (this.items_total === 0) {
                    const noResultsText = document.createElement('p');
                    noResultsText.className = 'no-results-text';
                    noResultsText.innerHTML = `No ${this.object_name} found`;
                    paginationContainer.appendChild(noResultsText);
                    return; // Exit early
                }

                // Create and append the "Previous" button
                const prevButton = document.createElement('button');
                prevButton.innerText = '← Previous';
                prevButton.disabled = this.current_page === 1;
                prevButton.className = 'px-4 py-2 text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50';
                prevButton.onclick = () => this.prevPage();
                paginationContainer.appendChild(prevButton);

                // Create and append page numbers
                for (let p = 1; p <= this.pages; p++) {
                    const pageButton = document.createElement('button');
                    pageButton.innerText = p.toString();
                    pageButton.className = `mx-1 px-3 py-1 rounded hover:bg-gray-200 ${this.current_page === p ? 'bg-blue-500 text-white' : 'bg-white text-gray-700'}`;
                    pageButton.onclick = () => this.gotoPage(p);
                    paginationContainer.appendChild(pageButton);
                }

                // Create and append the "Next" button
                const nextButton = document.createElement('button');
                nextButton.innerText = 'Next →';
                nextButton.disabled = this.current_page === this.pages;
                nextButton.className = 'px-4 py-2 text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50';
                nextButton.onclick = () => this.nextPage();
                paginationContainer.appendChild(nextButton);
            },
            prevPage() {
                if ((this.current_page + this.pageOffset) > 1) {
                    this.gotoPage(this.current_page - this.pageOffset);
                }
            },
            nextPage() {
                const goToNextPage = this.collectionCurrentlyViewed
                    ? this.collectionCurrentlyViewed < this.items_total
                    : (this.current_page + this.pageOffset) < this.pages

                if (goToNextPage) {
                    this.current_page = this.current_page + this.pageOffset;

                    if (this.showViewMore) {
                        const productsShown = this.collectionCurrentlyViewed
                            ? this.collectionCurrentlyViewed
                            : (this.current_page + this.pageOffset) * this.per_page;

                        if (productsShown <= this.items_total) {
                            this.currentItemsViewed = productsShown;
                        } else {
                            this.currentItemsViewed = this.items_total;
                        }

                        window.dispatchEvent(new CustomEvent('nextpage', {
                            detail: {
                                type: 'pagination',
                                page: this.current_page 
                            }
                        }));

                        this.renderViewMoreButton();
                    } else {
                        this.renderPagination();
                    }
                }
            },
            gotoPage(page) {
                this.current_page = page;
                this.renderPagination();
            },
        }
    }
}

export default pagination;
